import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '../../contexts/ColorModeContext';
import { services } from '../../pages/Services';

interface Command {
  section: 'Pages' | 'Services' | 'Actions' | 'Recent';
  label: string;
  path: string;
  run: (ctx: CommandContext) => void;
}

interface CommandContext {
  navigate: (path: string) => void;
  toggleTheme: () => void;
  mode: 'dark' | 'light';
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const SECTION_ORDER: Command['section'][] = ['Actions', 'Pages', 'Services'];

const CONTACT_EMAIL = 'contact@consultsre.com';

const CommandPalette: React.FC<Props> = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [announce, setAnnounce] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { mode, toggle } = useColorMode();

  const commands: Command[] = useMemo(() => {
    const base: Command[] = [
      { section: 'Pages', label: 'Home', path: '/', run: ctx => ctx.navigate('/') },
      { section: 'Pages', label: 'Services', path: '/services', run: ctx => ctx.navigate('/services') },
      { section: 'Pages', label: 'About', path: '/about-us', run: ctx => ctx.navigate('/about-us') },
      { section: 'Pages', label: 'Blog', path: '/blog', run: ctx => ctx.navigate('/blog') },
      { section: 'Pages', label: 'Contact', path: '/contact', run: ctx => ctx.navigate('/contact') },
      { section: 'Pages', label: 'Privacy Policy', path: '/privacy-policy', run: ctx => ctx.navigate('/privacy-policy') },
      { section: 'Pages', label: 'Terms & Conditions', path: '/terms', run: ctx => ctx.navigate('/terms') },
      {
        section: 'Actions',
        label: 'Book an assessment',
        path: '/contact',
        run: ctx => ctx.navigate('/contact'),
      },
    ];
    services.forEach(s => {
      base.push({
        section: 'Services',
        label: s.title,
        path: `/services/${s.id}`,
        run: ctx => ctx.navigate(`/services/${s.id}`),
      });
    });
    return base;
  }, []);

  // Actions that depend on current mode (recomputed each render so the label reflects state)
  const dynamicActions: Command[] = [
    {
      section: 'Actions',
      label: `Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`,
      path: 'action:theme',
      run: ctx => ctx.toggleTheme(),
    },
    {
      section: 'Actions',
      label: `Copy email · ${CONTACT_EMAIL}`,
      path: 'action:copy-email',
      run: () => {
        if (navigator.clipboard) {
          navigator.clipboard.writeText(CONTACT_EMAIL).catch(() => {});
        }
        setAnnounce('Copied email to clipboard.');
        setTimeout(() => setAnnounce(''), 1800);
      },
    },
  ];

  const allCommands: Command[] = [...commands, ...dynamicActions];

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setAnnounce('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allCommands;
    return allCommands.filter(c => c.label.toLowerCase().includes(q));
  }, [allCommands, query]);

  // Group by section while preserving order
  const grouped: Record<string, Command[]> = {};
  results.forEach(c => {
    (grouped[c.section] ||= []).push(c);
  });
  const sortedSections = [...SECTION_ORDER, 'Recent'].filter(s => grouped[s]?.length);

  const flat = sortedSections.flatMap(s => grouped[s]);

  const run = (cmd: Command) => {
    cmd.run({
      navigate: p => {
        onClose();
        navigate(p);
      },
      toggleTheme: () => {
        toggle();
        setAnnounce(`Switched to ${mode === 'dark' ? 'light' : 'dark'} mode.`);
        setTimeout(() => setAnnounce(''), 1400);
      },
      mode,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flat[active]) {
      e.preventDefault();
      run(flat[active]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  let globalIdx = -1;

  return (
    <div
      className="cmdk-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cmdk-panel">
        <div className="cmdk-input">
          <input
            ref={inputRef}
            type="text"
            placeholder="type a command or service…"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <kbd
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              padding: '2px 6px',
              border: '1px solid var(--line-strong)',
              borderRadius: 3,
              color: 'var(--ink-mute)',
            }}
          >
            esc
          </kbd>
        </div>
        <div className="cmdk-list">
          {sortedSections.map(section => (
            <React.Fragment key={section}>
              <div className="cmdk-section">{section}</div>
              {grouped[section].map(cmd => {
                globalIdx++;
                const isActive = globalIdx === active;
                const thisIdx = globalIdx;
                return (
                  <div
                    key={`${section}-${cmd.path}-${cmd.label}`}
                    className={`cmdk-item ${isActive ? 'active' : ''}`}
                    onMouseEnter={() => setActive(thisIdx)}
                    onClick={() => run(cmd)}
                  >
                    <span>{cmd.label}</span>
                    <span className="cmdk-path">{cmd.path}</span>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          {flat.length === 0 && (
            <div className="cmdk-item" style={{ color: 'var(--ink-mute)', cursor: 'default' }}>
              no matches
            </div>
          )}
        </div>
        <div
          aria-live="polite"
          style={{
            padding: '8px 18px',
            borderTop: '1px solid var(--line)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: announce ? 'var(--accent)' : 'var(--ink-mute)',
            minHeight: 28,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'space-between',
          }}
        >
          <span>{announce || `${flat.length} result${flat.length === 1 ? '' : 's'}`}</span>
          <span>↑↓ navigate · ↵ select</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
